/**
 * useRoles — derive the caller's Karexia roles for UI gating.
 *
 * Mirrors the backend (pubsocialnet_common.middleware.auth) through the shared
 * issuer-aware policy in `@/auth/claims`: a token from the legacy Karexia realm
 * keeps its realm roles (`realm_access.roles` + flat `roles` + client roles);
 * a token from the shared `core` realm counts ONLY the Karexia role client's
 * `resource_access.<roleClientId>.roles` — another application's ADMIN realm
 * role gives nothing here. Writes across settings (credits top-up, AI-registry
 * CRUD, BYOK keys) are admin-only server-side, so non-admins get a read-only UI.
 */

import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import { currentRolePolicy, decodeJwtPayload, resolveKarexiaRoles, type TokenClaims } from '@/auth/claims';
import { tr } from '@/i18n';

/** 3-tier registry write model (mirrors ai-orchestrator registry_api._registry_tier). */
export type RegistryTier = 'global' | 'cabinet' | 'user';

/** Which registry entities each tier may create/edit/delete. */
const TIER_WRITABLE: Record<RegistryTier, ReadonlySet<string>> = {
  global: new Set(['agents', 'models', 'skills', 'prompts', 'tools']),
  cabinet: new Set(['skills', 'prompts', 'tools']),
  user: new Set(['prompts']),
};

export interface RolesInfo {
  roles: string[];
  isAdmin: boolean;
  /** Cross-tenant platform owner (`platform_superadmin`). Some backend gates (vouchers,
   *  plan admin) accept it WITHOUT the per-tenant `admin` role; platform-wide pricing
   *  levers (global margin) require it. */
  isSuperadmin: boolean;
  /** `owner` OR `admin` — the gate of tenant-scoped writes on rbac-service (`require_write`)
   *  and tenant-service (`require_platform_admin`). A business owner is NOT an `admin`. */
  isOwnerOrAdmin: boolean;
  /** `owner` OR `admin` OR `platform_superadmin` — the server gate of tenant BILLING
   *  (subscribe/cancel/trial/portal/add-ons/credit packs) and of AI-learning
   *  opt-in / erasure (ai-orchestrator `require_owner_or_admin`, security I0 S2/S6). */
  canManageAccount: boolean;
  /** Any tenant WRITE role (owner/admin/manager/member/editor/publisher/content_*,
   *  superadmin). False for `viewer`/client accounts, which are read-only: the server
   *  refuses AI runs, hiring, trigger runs, delegation… with 403 (security I0 S5). */
  isWriter: boolean;
  /** Registry write tier: admin→global, owner→cabinet, else→user. */
  tier: RegistryTier;
  /** True if this tier may create/edit/delete the given registry entity. */
  canWrite: (entity: string) => boolean;
}

// Business/pro CLIENT + client-ADMIN roles author their own cabinet-scoped
// skills/prompts/tools — mirrors ai-orchestrator registry_api._CABINET_ROLES.
const CABINET_ROLES = new Set([
  'owner', 'business', 'business-admin', 'business_admin',
  'client-admin', 'client_admin', 'clientadmin',
  'cabinet-admin', 'cabinet_admin',
]);

/** Mirrors ai-orchestrator `routes/_authz.WRITE_OR_SERVICE_ROLES` (= common
 *  TENANT_WRITE_ROLES + the cabinet author roles, minus the S2S `service`). */
export const WRITE_ROLES: ReadonlySet<string> = new Set([
  'admin', 'owner', 'manager', 'member', 'editor', 'publisher',
  'content_creator', 'content_editor', 'content_manager', 'content_publisher',
  'platform_superadmin',
  'business', 'business-admin', 'business_admin',
  'client-admin', 'client_admin', 'clientadmin',
  'cabinet-admin', 'cabinet_admin',
]);

/** Tooltip/explanation for a control hidden from non owner/admin roles (S2/S6). */
export const OWNER_ADMIN_ONLY_HINT = tr('routes.settings.useRoles.reservedForTheAccountOwnerOr');
/** Tooltip/explanation for a control disabled for read-only (viewer/client) roles (S5). */
export const READ_ONLY_HINT = tr('routes.settings.useRoles.readOnlyAccessReservedForMembers');

function tierFrom(roles: string[]): RegistryTier {
  if (roles.includes('admin')) return 'global';
  if (roles.some((r) => CABINET_ROLES.has(r))) return 'cabinet';
  return 'user';
}

export function useRoles(): RolesInfo {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const profile = auth.user?.profile;

  return useMemo(() => {
    const policy = currentRolePolicy();
    const fromToken = accessToken ? resolveKarexiaRoles(decodeJwtPayload(accessToken), policy) : [];
    const roles = fromToken.length
      ? fromToken
      : resolveKarexiaRoles((profile ?? null) as unknown as TokenClaims | null, policy);
    const tier = tierFrom(roles);
    return {
      roles,
      isAdmin: roles.includes('admin'),
      isSuperadmin: roles.includes('platform_superadmin'),
      isOwnerOrAdmin: roles.includes('admin') || roles.includes('owner'),
      canManageAccount:
        roles.includes('admin') || roles.includes('owner') || roles.includes('platform_superadmin'),
      isWriter: roles.some((r) => WRITE_ROLES.has(r)),
      tier,
      canWrite: (entity: string) => TIER_WRITABLE[tier].has(entity),
    };
  }, [accessToken, profile]);
}
